import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GoalView } from '@goyone/shared';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { EmptyState, ErrorState, Modal, PageHead } from '../../components/ui';
import { OwnerChip, useTeamDirectory } from '../../components/People';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';

/**
 * الأهداف — كأهداف أسانا، والنسبة لا تُكتب باليد.
 *
 * تقدّم الهدف محسوبٌ من مهام مشاريعه المربوطة به لحظة القراءة: يصدُق أو
 * يفضح، وكلاهما أنفع من رقمٍ يُحدَّث قبل الاجتماع. المشروع يُربط بهدفه من
 * نافذة «تعديل المشروع».
 */
export function AgencyGoals() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { atLeast } = useAuth();
  const [editing, setEditing] = useState<GoalView | 'new' | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const goals = useQuery({
    queryKey: ['agency', 'goals'],
    queryFn: () => api<GoalView[]>('/agency/goals'),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['agency'] });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/agency/goals/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      toast('حُذف الهدف — مشاريعه باقية');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  const archive = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api(`/agency/goals/${id}`, { method: 'PATCH', body: { archived } }),
    onSuccess: refresh,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const rows = (goals.data ?? []).filter((g) => (showArchived ? true : !g.archived));

  return (
    <div className="page">
      <PageHead
        title="الأهداف"
        subtitle="أهداف الشركة وتقدّمها — محسوبًا من مهام المشاريع التي تخدمها، لا من رقمٍ يُكتب"
        actions={
          <>
            <label className="row tiny mute" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              إظهار المؤرشفة
            </label>
            {atLeast('MANAGER') && (
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                <Icon name="target" size={15} />
                هدف جديد
              </button>
            )}
          </>
        }
      />

      {goals.isError && <ErrorState error={goals.error} onRetry={() => void goals.refetch()} />}
      {goals.isPending && <div className="skeleton" style={{ height: 140 }} />}

      {goals.data && rows.length === 0 && (
        <EmptyState
          title="لا أهداف بعد"
          text="أنشئ هدفًا (كـ«40 عميلًا محتملًا شهريًّا») ثم اربط به المشاريع التي تخدمه من نافذة تعديل المشروع."
        />
      )}

      {/*
        رأسُ الصفحة: أين تقف الأهدافُ مجتمعةً قبل تفصيلها.
        هدفٌ بلا مشاريعَ تخدمه رقمٌ لا يتحرّك، فيُعدّ ويُقال.
      */}
      {rows.length > 0 && (
        <div className="ag-stats">
          <span className="ag-stat">
            <Icon name="target" size={18} />
            <strong className="num">{rows.filter((g) => !g.archived).length}</strong>
            <span>هدفًا قائمًا</span>
          </span>
          <span className="ag-stat">
            <Icon name="briefcase" size={18} />
            <strong className="num">{rows.reduce((s, g) => s + g.projectCount, 0)}</strong>
            <span>مشروعًا يخدمها</span>
          </span>
          <span className="ag-stat">
            <Icon name="check" size={18} />
            <strong className="num">
              {(() => {
                const t = rows.reduce((s, g) => s + g.taskCount, 0);
                const d = rows.reduce((s, g) => s + g.doneCount, 0);
                return t ? Math.round((d / t) * 100) : 0;
              })()}
              ٪
            </strong>
            <span>متوسّط الإنجاز</span>
          </span>
          <span
            className={`ag-stat${rows.some((g) => g.projectCount === 0) ? ' is-alert' : ''}`}
          >
            <Icon name="alert" size={18} />
            <strong className="num">{rows.filter((g) => g.projectCount === 0).length}</strong>
            <span>بلا مشاريع تخدمه</span>
          </span>
        </div>
      )}

      <div className="ag-goals">
        {rows.map((g) => {
          const pct = g.taskCount ? Math.round((g.doneCount / g.taskCount) * 100) : 0;
          /* قوسُ الإنجاز: النسبةُ تُرى مساحةً قبل أن تُقرأ رقمًا. */
          const R = 26;
          const C = 2 * Math.PI * R;
          return (
            <section key={g.id} className={`card${g.archived ? ' ag-archived' : ''}`}>
              <div className="card-body">
                <div className="row" style={{ gap: 'var(--sp-2)', alignItems: 'flex-start' }}>
                  <svg viewBox="0 0 64 64" className="goal-ring" aria-hidden="true">
                    <circle cx="32" cy="32" r={R} className="goal-ring-track" />
                    <circle
                      cx="32"
                      cy="32"
                      r={R}
                      className="goal-ring-fill"
                      strokeDasharray={`${(pct / 100) * C} ${C}`}
                    />
                    <text x="32" y="37" className="goal-ring-num">
                      {pct}٪
                    </text>
                  </svg>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong>{g.title}</strong>
                    {g.description && <p className="tiny mute">{g.description}</p>}
                    <div className="row mt-2" style={{ gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
                      <OwnerChip ownerId={g.ownerId} size={20} empty="بلا مالك" />
                      {g.dueAt && (
                        <span className="tiny mute num">
                          <Icon name="clock" size={12} /> {date(g.dueAt)}
                        </span>
                      )}
                      <span className="tiny mute num">
                        {g.projectCount} مشروع · {g.doneCount}/{g.taskCount} مهمة
                      </span>
                    </div>
                  </div>
                  {atLeast('MANAGER') && (
                    <span className="row" style={{ gap: 4 }}>
                      <button
                        className="btn btn-sm btn-icon"
                        onClick={() => setEditing(g)}
                        aria-label="تعديل الهدف"
                      >
                        <Icon name="edit" size={13} />
                      </button>
                      <button
                        className="btn btn-sm btn-icon"
                        onClick={() => archive.mutate({ id: g.id, archived: !g.archived })}
                        aria-label={g.archived ? 'إلغاء الأرشفة' : 'أرشفة'}
                        title={g.archived ? 'إلغاء الأرشفة' : 'أرشفة'}
                      >
                        <Icon name="inbox" size={13} />
                      </button>
                      <button
                        className="btn btn-sm btn-icon"
                        onClick={() => {
                          if (window.confirm(`حذف هدف «${g.title}»؟`)) remove.mutate(g.id);
                        }}
                        aria-label="حذف"
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </span>
                  )}
                </div>
                <div className="ag-progress mt-2" title={`${g.doneCount}/${g.taskCount}`}>
                  <span className="ag-progress-fill" style={{ width: `${pct}%` }} />
                </div>
                {g.projectCount === 0 && (
                  <p className="tiny mute mt-2">
                    لا مشاريع تخدمه بعد — اربطها من نافذة «تعديل المشروع» فتُحسب نسبتُه.
                  </p>
                )}
              </div>
            </section>
          );
        })}
      </div>

      {editing && (
        <GoalModal
          goal={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function GoalModal({
  goal,
  onClose,
  onSaved,
}: {
  goal: GoalView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const { assignable } = useTeamDirectory();
  const [title, setTitle] = useState(goal?.title ?? '');
  const [description, setDescription] = useState(goal?.description ?? '');
  const [ownerId, setOwnerId] = useState(goal?.ownerId ?? '');
  const [dueAt, setDueAt] = useState(goal?.dueAt ? goal.dueAt.slice(0, 10) : '');

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        ownerId: ownerId || null,
        dueAt: dueAt || null,
      };
      return goal
        ? api(`/agency/goals/${goal.id}`, { method: 'PATCH', body })
        : api('/agency/goals', { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  return (
    <Modal title={goal ? 'تعديل الهدف' : 'هدف جديد'} onClose={onClose}>
      <label className="field">
        <span className="label">عنوان الهدف</span>
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="مثل: تقليل المشكلات التشغيلية المتكررة"
          autoFocus
        />
      </label>
      <label className="field">
        <span className="label">الوصف (اختياري)</span>
        <textarea
          className="textarea"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      <div className="row" style={{ gap: 'var(--sp-3)' }}>
        <label className="field" style={{ flex: 1 }}>
          <span className="label">مالك الهدف</span>
          <select className="select" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            <option value="">بلا مالك</option>
            {assignable.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span className="label">الموعد</span>
          <input
            type="date"
            className="input"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
          />
        </label>
      </div>
      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary"
          onClick={() => save.mutate()}
          disabled={title.trim().length < 2 || save.isPending}
        >
          {goal ? 'حفظ' : 'أنشئ الهدف'}
        </button>
        <button className="btn" onClick={onClose}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}
